import { BOARD_SIZE, isInsideBoard } from './board';
import type { Board, BoardPosition, Piece, PlayerNumber, ValidMove } from './types';

const ALL_DIRECTIONS = [
  [-1, -1],
  [-1, 1],
  [1, -1],
  [1, 1],
] as const;

export function isKing(piece: Piece): boolean {
  return piece === 3 || piece === 4;
}

export function getPieceOwner(piece: Piece): PlayerNumber | null {
  if (piece === 1 || piece === 3) return 1;
  if (piece === 2 || piece === 4) return 2;
  return null;
}

export function isPlayerPiece(piece: Piece, player: PlayerNumber): boolean {
  return getPieceOwner(piece) === player;
}

export function isOpponentPiece(board: Board, row: number, col: number, player: PlayerNumber): boolean {
  if (!isInsideBoard(row, col)) return false;

  const owner = getPieceOwner(board[row][col]);
  return owner !== null && owner !== player;
}

export function calculateValidMoves(
  board: Board,
  row: number,
  col: number,
  options: {
    onlyCaptures?: boolean;
    currentPlayer?: PlayerNumber;
  } = {}
): ValidMove[] {
  if (!isInsideBoard(row, col)) return [];

  const piece = board[row][col];
  const player = options.currentPlayer ?? getPieceOwner(piece);
  if (!player || piece === 0) return [];

  const moves: ValidMove[] = [];

  if (isKing(piece)) {
    ALL_DIRECTIONS.forEach(([dr, dc]) => {
      addKingCaptures(board, moves, row, col, dr, dc, player);
    });
  } else {
    ALL_DIRECTIONS.forEach(([dr, dc]) => {
      addNormalCapture(board, moves, row, col, dr, dc, player);
    });
  }

  if (options.onlyCaptures || moves.length > 0) {
    return moves;
  }

  if (isKing(piece)) {
    ALL_DIRECTIONS.forEach(([dr, dc]) => {
      addKingQuietMoves(board, moves, row, col, dr, dc);
    });
    return moves;
  }

  const forwardDirections = player === 1
    ? [[1, -1], [1, 1]]
    : [[-1, -1], [-1, 1]];

  forwardDirections.forEach(([dr, dc]) => {
    const nextRow = row + dr;
    const nextCol = col + dc;

    if (isInsideBoard(nextRow, nextCol) && board[nextRow][nextCol] === 0) {
      moves.push({ row: nextRow, col: nextCol });
    }
  });

  return moves;
}

export function findMandatoryPieces(board: Board, currentPlayer: PlayerNumber): BoardPosition[] {
  const pieces: BoardPosition[] = [];

  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (!isPlayerPiece(board[row][col], currentPlayer)) continue;

      const moves = calculateValidMoves(board, row, col, {
        currentPlayer,
        onlyCaptures: true,
      });

      if (moves.some((move) => move.isCapture)) {
        pieces.push({ row, col });
      }
    }
  }

  return pieces;
}

function addKingCaptures(
  board: Board,
  moves: ValidMove[],
  row: number,
  col: number,
  dr: number,
  dc: number,
  player: PlayerNumber
) {
  let currentRow = row + dr;
  let currentCol = col + dc;
  let opponent: BoardPosition | null = null;

  while (isInsideBoard(currentRow, currentCol)) {
    const piece = board[currentRow][currentCol];

    if (piece !== 0) {
      if (!opponent && isOpponentPiece(board, currentRow, currentCol, player)) {
        opponent = { row: currentRow, col: currentCol };
        currentRow += dr;
        currentCol += dc;
        continue;
      }

      break;
    }

    if (opponent) {
      moves.push({
        row: currentRow,
        col: currentCol,
        isCapture: true,
        capturedRow: opponent.row,
        capturedCol: opponent.col,
      });
    }

    currentRow += dr;
    currentCol += dc;
  }
}

function addNormalCapture(
  board: Board,
  moves: ValidMove[],
  row: number,
  col: number,
  dr: number,
  dc: number,
  player: PlayerNumber
) {
  const jumpRow = row + dr;
  const jumpCol = col + dc;
  const captureRow = row + dr * 2;
  const captureCol = col + dc * 2;

  if (
    isInsideBoard(captureRow, captureCol) &&
    isOpponentPiece(board, jumpRow, jumpCol, player) &&
    board[captureRow][captureCol] === 0
  ) {
    moves.push({
      row: captureRow,
      col: captureCol,
      isCapture: true,
      capturedRow: jumpRow,
      capturedCol: jumpCol,
    });
  }
}

function addKingQuietMoves(
  board: Board,
  moves: ValidMove[],
  row: number,
  col: number,
  dr: number,
  dc: number
) {
  let currentRow = row + dr;
  let currentCol = col + dc;

  while (isInsideBoard(currentRow, currentCol) && board[currentRow][currentCol] === 0) {
    moves.push({ row: currentRow, col: currentCol });
    currentRow += dr;
    currentCol += dc;
  }
}
